import { DAppKitProvider } from '@mysten/dapp-kit-react'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import Decimal from 'decimal.js'
import { ErrorBoundary } from '@sentry/react'

import { GlobalStyles } from 'styles/GlobalStyles'
import { Toast } from 'components/Toast/Toast'
import { router } from 'utils/routes'
import { SwapRouterProvider } from 'contexts/SwapRouterContext'
import { BackendClientProvider } from '@/shared/kai/contexts/BackendClientContext'
import { ThemeProvider } from 'contexts/ThemeProviderContext'
import { ApiResponseError } from '@/shared/backend/client'
import { PostHogLoadProvider } from 'components/PostHogLoadProvider/PostHogLoadProvider'
import { WalletPostHogIdentify } from 'components/WalletPostHogIdenfity/WalletPostHogIdenfity'
import { ViewAsWalletProvider } from 'contexts/ViewAsWalletContext'
import { initializeSentry, logRequestError } from 'utils/sentry-utils'
import { WalletSentryInit } from 'components/WalletSentryInit/WalletSentryInit'
import { SentryFallbackPage } from 'components/SentryFallbackPage/SentryFallbackPage'
import { TermsAcceptanceProvider } from 'modules/TermsAcceptance/TermsAcceptanceContext'
import { dAppKit } from './dappKit'

initializeSentry()

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      logRequestError(error, query)
    },
  }),
  defaultOptions: {
    queries: {
      // Don't retry if it's a client error (4xx) except for 404
      retry: (failureCount: number, error: unknown) => {
        if (error instanceof ApiResponseError) {
          if (error.isClientError() && error.status !== 404) {
            return false
          }
        }
        return failureCount < 3
      },
    },
  },
})

Decimal.set({
  toExpNeg: -1000, // Very small negative exponent
  toExpPos: 1000, // Very large positive exponent
})

export function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary fallback={({ eventId }) => <SentryFallbackPage eventId={eventId} />}>
        <PostHogLoadProvider>
          <QueryClientProvider client={queryClient}>
            <BackendClientProvider baseUrl={import.meta.env.VITE_KAI_API_URL}>
              <SwapRouterProvider>
                <ViewAsWalletProvider>
                  <ReactQueryDevtools initialIsOpen={true} />
                  <Toast />
                  <GlobalStyles />
                  <DAppKitProvider dAppKit={dAppKit}>
                    <TermsAcceptanceProvider>
                      <WalletPostHogIdentify />
                      <WalletSentryInit />
                      <RouterProvider router={router} />
                    </TermsAcceptanceProvider>
                  </DAppKitProvider>
                </ViewAsWalletProvider>
              </SwapRouterProvider>
            </BackendClientProvider>
          </QueryClientProvider>
        </PostHogLoadProvider>
      </ErrorBoundary>
    </ThemeProvider>
  )
}
